import { fmtTimestamp } from '@/lib/http'
import type { TgeRunSummary } from '@/lib/tge'

import { RunStatus, gridLabel } from './TgeReports'

/** Brief line for a history row: totals once finished, progress while running. */
function totalsBrief(r: TgeRunSummary): string {
  const t = r.totals
  if (t) return `${t.issues} issues · ${t.rows} rows · ${t.warnings} warnings`
  if (r.finished_at) return 'stopped before writing — open it for the reason'
  const p = r.progress
  if (p?.stage === 'writing') return `${p.done} of ${p.total ?? '?'} issues done`
  if (p?.stage === 'reading') return `reading the grid — ${p.done} issues`
  if (p?.stage === 'resolving') return 'resolving issue keys'
  return 'in progress'
}

/** Last runs, newest first; a click opens the run's report in the center. */
export function TgeHistory({ runs, error, activeId, disabled, onOpen, onRetry }: {
  runs: TgeRunSummary[] | null
  error: string | null
  activeId: number | null
  disabled: boolean
  onOpen: (id: number) => void
  onRetry: () => void
}) {
  return (
    <div>
      {error && (
        <div role="alert" className="mb-2 rounded-md border border-line px-3 py-2 text-[12px]">
          <p className="text-foreground">
            Run history failed to load{runs !== null && runs.length > 0 ? ' — the list below may be stale' : ''}.
          </p>
          <p className="mt-0.5 break-words font-mono text-[11px] text-danger">{error}</p>
          <button
            type="button"
            onClick={onRetry}
            className="mt-2 rounded-md border border-line-strong bg-surface-raised px-2.5 py-0.5 text-[12px] text-foreground hover:bg-surface-active"
          >
            Retry
          </button>
        </div>
      )}
      {runs === null
        ? (!error && (
          <div className="space-y-2" aria-hidden>
            {Array.from({ length: 3 }).map((_, i) => <div key={i} className="skeleton" style={{ height: 40 }} />)}
          </div>
        ))
        : runs.length === 0
          ? (!error && <p className="text-[12.5px] text-text-2">No migration runs yet.</p>)
          : (
            <ul className="space-y-0.5">
              {runs.map((r) => {
                const active = r.id === activeId
                return (
                  <li key={r.id}>
                    <button
                      type="button"
                      onClick={() => onOpen(r.id)}
                      disabled={disabled}
                      title={disabled ? 'Available when the current run finishes' : undefined}
                      aria-label={`Open report for run ${r.id}`}
                      aria-current={active ? 'true' : undefined}
                      className={[
                        // disabled while a run is in progress: muted text and a not-allowed cursor
                        'relative block w-full rounded-[5px] px-2 py-1.5 text-left transition-colors disabled:cursor-not-allowed disabled:[&_span]:!text-text-3',
                        active
                          ? 'bg-surface-selected before:absolute before:inset-y-0 before:left-0 before:w-0.5 before:rounded-l-[5px] before:bg-primary'
                          : 'hover:bg-surface-raised',
                      ].join(' ')}
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="font-mono text-[12.5px] text-foreground">#{r.id}</span>
                        <span className="text-[12.5px] text-text-2">{r.dry_run ? 'dry run' : 'live'}</span>
                        <span className="ml-auto"><RunStatus status={r.status} /></span>
                      </span>
                      <span className="block truncate text-[11.5px] text-text-2">{totalsBrief(r)}</span>
                      <span className="block truncate font-mono text-[11px] text-text-3">
                        {gridLabel(r.grid_id, r.table_id)} · {fmtTimestamp(r.started_at)}
                      </span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
    </div>
  )
}
